/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Footer from '../components/Footer';

const MasainMedia = () => {
  const [mediaCoverage, setMediaCoverage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const API_MASA_MEDIA = process.env.REACT_APP_API_BASE_URL + "/UImasa-media";
  const API_MEDIA = process.env.REACT_APP_API_BASE_URL + "/UImedia";

  // Function to format date to display full month name
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // If date is already in "Month Day" format, return as is
      if (typeof dateString === 'string' && /[a-zA-Z]/.test(dateString)) {
        return dateString;
      }
      
      // If it's a date string that can be parsed
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if invalid date
      }
      
      // Format to "Month Day, Year" (e.g., "January 15, 2025")
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Fetch media coverage data from API
  const fetchMediaCoverage = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_MASA_MEDIA);
      let mediaData = res.data.results || res.data;

      // Filter only active media entries
      const activeMedia = mediaData.filter(item => item.isActive);

      // Format dates for each media entry
      const mediaWithFormattedDates = activeMedia.map(mediaItem => ({
        ...mediaItem,
        formattedDate: formatDate(mediaItem.date)
      }));

      // Load images for each media entry
      const mediaWithImages = await Promise.all(
        mediaWithFormattedDates.map(async (mediaItem) => {
          if (mediaItem.image?.id) {
            try {
              const imgRes = await axios.get(`${API_MEDIA}/${mediaItem.image.id}/download`, {
                responseType: "blob",
              });
              mediaItem.imageUrl = URL.createObjectURL(imgRes.data);
            } catch (err) {
              console.error("Error loading image for", mediaItem.title, err);
              mediaItem.imageUrl = "https://via.placeholder.com/400x200.png?text=No+Image";
            }
          } else {
            mediaItem.imageUrl = "https://via.placeholder.com/400x200.png?text=No+Image";
          }
          return mediaItem;
        })
      );

      setMediaCoverage(mediaWithImages);
    } catch (err) {
      console.error("Error fetching media coverage:", err);
      setError("Failed to load media coverage. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMediaCoverage();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying || mediaCoverage.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % mediaCoverage.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, mediaCoverage.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % mediaCoverage.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + mediaCoverage.length) % mediaCoverage.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Loading media coverage...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button 
            onClick={fetchMediaCoverage}
            className="bg-yellow-500 text-black font-bold px-6 py-3 rounded-full hover:bg-yellow-600 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (mediaCoverage.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-300 text-lg mb-4">No media coverage available at the moment.</p>
          <p className="text-gray-400">Check back later for updates.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Hero Carousel Section */}
      <div 
        className="relative h-96 overflow-hidden"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        <AnimatePresence mode='wait'>
          <motion.div
            key={currentSlide}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/50 z-10"></div>
            <img 
              src={mediaCoverage[currentSlide].imageUrl} 
              alt={mediaCoverage[currentSlide].title} 
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 z-20 p-8 max-w-3xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <span className="bg-yellow-500 text-black text-sm font-bold px-3 py-1 rounded-full mb-4 inline-block">
                  Featured Coverage
                </span>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">{mediaCoverage[currentSlide].title}</h1>
                <p className="text-gray-300 text-lg mb-6 line-clamp-2">{mediaCoverage[currentSlide].description}</p>
                <a 
                  href={mediaCoverage[currentSlide].link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-yellow-500 text-black font-bold px-6 py-3 rounded-full hover:bg-yellow-600 transition"
                >
                  Read Full Story
                </a>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
          aria-label="Previous slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
          aria-label="Next slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Carousel Indicators */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30 flex space-x-2">
          {mediaCoverage.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-yellow-500' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-600 z-30">
          <motion.div
            className="h-full bg-yellow-500"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 5, ease: "linear" }}
            key={currentSlide}
          />
        </div>
      </div>

      {/* Media Coverage Grid */}
      <div className="py-16 px-6 max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-4">
            MASA in Media
          </h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-4"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See how media outlets are covering our initiatives and impact
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {mediaCoverage.map((item, index) => (
            <motion.div
              key={item.id}
              className="bg-gray-800 rounded-xl overflow-hidden shadow-2xl hover:shadow-yellow-500/20 transition-all"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="h-48 overflow-hidden">
                <img 
                  src={item.imageUrl} 
                  alt={item.title} 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-yellow-400 font-medium">{item.source}</span>
                  <span className="text-gray-400 text-sm">
                    {item.formattedDate || formatDate(item.date)}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-3 line-clamp-2">{item.title}</h3>
                <p className="text-gray-300 mb-4 line-clamp-3">{item.description}</p>
                <a 
                  href={item.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-medium"
                >
                  Read More
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Press Inquiries Section */}
      <div className="py-16 px-6 bg-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-yellow-400 mb-6">Press Inquiries</h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              For media inquiries, interview requests, or press releases, please contact our communications team.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="mailto:press@masa.org" 
                className="bg-yellow-500 text-black font-bold px-6 py-3 rounded-full hover:bg-yellow-600 transition"
              >
                Email Press Team
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MasainMedia;





// old code


// import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import Footer from '../components/Footer';

// const MasainMedia = () => {
//   // Sample media coverage data
//   const mediaCoverage = [
//     {
//       id: 8,
//       title: "Loan & Finance Mela & BFSI Conclave 2025 – Kolkata",
//       source: "Dailyhunt, AajTalkss, Akhbaar e mashriq",
//       date: "July 17",
//       description: "India's largest MSME-focused financial event 25+ Banks, NBFCs, Fintechs & Funding Experts On-the-spot loan assistance & financial literacy sessions Explore business funding opportunities under one roof",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/masa-in-media-imgs/newsphoto.png",
//       link: "https://dhunt.in/119oYk"
//     },
//     {
//       id: 0,
//       title: "East India Career & Job Fair 2025, Kolkata",
//       source: "YuvaShakti News",
//       date: "4th - 5th May",
//       description: "Student at Eastern India Job Fair & Career Expo at IICR on Saturday. The two-day Eastern India Job Fair & Career Expo Kolkata 2025, in association with MSME & Startup Accelerator Pvt Ltd (MASA) and the Confederation of West Bengal Trade Association (CWBTA), connected prospective job seekers with employers.",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/masa-in-media-imgs/job-fair.webp",
//       link: "https://masaforum.com/MasainMedia"
//     },
//     {
//       id: 1,
//       title: "Global Education Conclave 2025",
//       source: "The Statesman",
//       date: "February 15, 2025",
//       description: "The MSME & Startup Accelerator Awards (MASA) 2025 - Kolkata Edition was a night of inspiration, honouring exceptional entrepreneurs and industry leaders at Silpo Sadan, Camac Street",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/masa-in-media-imgs/Statesman.webp",
//       link: "https://masaforum.com/MasainMedia"
//     },
//     {
//       id: 2,
//       title: "Coverage of our Excellence awards in MSMEs & Startups.",
//       source: "Prabhat Khabar",
//       date: "February 8, 2025",
//       description: "The MSME & Startup Accelerator Awards 2025 – Kolkata Edition took place at Silpo Sadan, Camac Street, celebrating and honouring distinguished entrepreneurs and industry leaders in an evening filled with inspiration.",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/masa-in-media-imgs/prabhat.webp",
//       link: "https://masaforum.com/MasainMedia"
//     }
//   ];

//   const [currentSlide, setCurrentSlide] = useState(0);
//   const [isAutoPlaying, setIsAutoPlaying] = useState(true);

//   // Auto-advance carousel
//   useEffect(() => {
//     if (!isAutoPlaying) return;

//     const interval = setInterval(() => {
//       setCurrentSlide((prev) => (prev + 1) % mediaCoverage.length);
//     }, 5000); // Change slide every 5 seconds

//     return () => clearInterval(interval);
//   }, [isAutoPlaying, mediaCoverage.length]);

//   const nextSlide = () => {
//     setCurrentSlide((prev) => (prev + 1) % mediaCoverage.length);
//   };

//   const prevSlide = () => {
//     setCurrentSlide((prev) => (prev - 1 + mediaCoverage.length) % mediaCoverage.length);
//   };

//   const goToSlide = (index) => {
//     setCurrentSlide(index);
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
//       {/* Hero Carousel Section */}
//       <div 
//         className="relative h-96 overflow-hidden"
//         onMouseEnter={() => setIsAutoPlaying(false)}
//         onMouseLeave={() => setIsAutoPlaying(true)}
//       >
//         <AnimatePresence mode='wait'>
//           <motion.div
//             key={currentSlide}
//             className="absolute inset-0"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             transition={{ duration: 0.8 }}
//           >
//             <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/50 z-10"></div>
//             <img 
//               src={mediaCoverage[currentSlide].image} 
//               alt={mediaCoverage[currentSlide].title} 
//               className="w-full h-full object-cover"
//             />
//             <div className="absolute bottom-0 left-0 z-20 p-8 max-w-3xl">
//               <motion.div
//                 initial={{ opacity: 0, y: 20 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ duration: 0.8, delay: 0.3 }}
//               >
//                 <span className="bg-yellow-500 text-black text-sm font-bold px-3 py-1 rounded-full mb-4 inline-block">
//                   Featured Coverage
//                 </span>
//                 <h1 className="text-4xl md:text-5xl font-bold mb-4">{mediaCoverage[currentSlide].title}</h1>
//                 <p className="text-gray-300 text-lg mb-6 line-clamp-2">{mediaCoverage[currentSlide].description}</p>
//                 <a 
//                   href={mediaCoverage[currentSlide].link} 
//                   target="_blank" 
//                   rel="noopener noreferrer"
//                   className="inline-flex items-center bg-yellow-500 text-black font-bold px-6 py-3 rounded-full hover:bg-yellow-600 transition"
//                 >
//                   Read Full Story
//                 </a>
//               </motion.div>
//             </div>
//           </motion.div>
//         </AnimatePresence>

//         {/* Carousel Controls */}
//         <button
//           onClick={prevSlide}
//           className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
//           aria-label="Previous slide"
//         >
//           <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
//           </svg>
//         </button>

//         <button
//           onClick={nextSlide}
//           className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
//           aria-label="Next slide"
//         >
//           <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
//           </svg>
//         </button>

//         {/* Carousel Indicators */}
//         <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30 flex space-x-2">
//           {mediaCoverage.map((_, index) => (
//             <button
//               key={index}
//               onClick={() => goToSlide(index)}
//               className={`w-3 h-3 rounded-full transition-all ${
//                 index === currentSlide ? 'bg-yellow-500' : 'bg-white/50'
//               }`}
//               aria-label={`Go to slide ${index + 1}`}
//             />
//           ))}
//         </div>

//         {/* Progress Bar */}
//         <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-600 z-30">
//           <motion.div
//             className="h-full bg-yellow-500"
//             initial={{ width: "0%" }}
//             animate={{ width: "100%" }}
//             transition={{ duration: 5, ease: "linear" }}
//             key={currentSlide}
//           />
//         </div>
//       </div>

//       {/* Media Coverage Grid */}
//       <div className="py-16 px-6 max-w-7xl mx-auto">
//         <motion.div 
//           className="text-center mb-16"
//           initial={{ opacity: 0, y: 50 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.8 }}
//           viewport={{ once: true }}
//         >
//           <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-4">
//             MASA in Media
//           </h2>
//           <div className="w-24 h-1 bg-yellow-500 mx-auto mb-4"></div>
//           <p className="text-xl text-gray-300 max-w-3xl mx-auto">
//             See how media outlets are covering our initiatives and impact
//           </p>
//         </motion.div>

//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//           {mediaCoverage.map((item, index) => (
//             <motion.div
//               key={item.id}
//               className="bg-gray-800 rounded-xl overflow-hidden shadow-2xl hover:shadow-yellow-500/20 transition-all"
//               initial={{ opacity: 0, y: 50 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.5, delay: index * 0.1 }}
//               viewport={{ once: true }}
//               whileHover={{ y: -5 }}
//             >
//               <div className="h-48 overflow-hidden">
//                 <img 
//                   src={item.image} 
//                   alt={item.title} 
//                   className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
//                 />
//               </div>
//               <div className="p-6">
//                 <div className="flex justify-between items-start mb-2">
//                   <span className="text-yellow-400 font-medium">{item.source}</span>
//                   <span className="text-gray-400 text-sm">{item.date}</span>
//                 </div>
//                 <h3 className="text-xl font-bold mb-3 line-clamp-2">{item.title}</h3>
//                 <p className="text-gray-300 mb-4 line-clamp-3">{item.description}</p>
//                 <a 
//                   href={item.link} 
//                   target="_blank" 
//                   rel="noopener noreferrer"
//                   className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-medium"
//                 >
//                   Read More
//                   <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
//                   </svg>
//                 </a>
//               </div>
//             </motion.div>
//           ))}
//         </div>
//       </div>

//       {/* Press Inquiries Section */}
//       <div className="py-16 px-6 bg-gray-800">
//         <div className="max-w-4xl mx-auto text-center">
//           <motion.div
//             initial={{ opacity: 0 }}
//             whileInView={{ opacity: 1 }}
//             transition={{ duration: 0.8 }}
//             viewport={{ once: true }}
//           >
//             <h2 className="text-3xl font-bold text-yellow-400 mb-6">Press Inquiries</h2>
//             <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
//               For media inquiries, interview requests, or press releases, please contact our communications team.
//             </p>
//             <div className="flex flex-col sm:flex-row justify-center gap-4">
//               <a 
//                 href="mailto:press@masa.org" 
//                 className="bg-yellow-500 text-black font-bold px-6 py-3 rounded-full hover:bg-yellow-600 transition"
//               >
//                 Email Press Team
//               </a>
//             </div>
//           </motion.div>
//         </div>
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default MasainMedia;